import React, { useContext, useState } from "react"
import tw from "twin.macro"
import { LocationContext } from "../../../contexts/Location"
import { setRadius } from "../../../contexts/Location/actions"
import { RadiusSelectorProps } from "./RadiusSelector.d"
import { DistanceSlider } from "../../../components/atoms/DistanceSlider"
import { FiltersContext } from "../../../contexts/Filters/context"
import { setSelectedFilterOption } from "../../../contexts/Filters/actions"
import { useTealiumContext } from "../../../contexts/Tealium"
import useTealiumEvent from "../../../hooks/Tealium/useTealiumEvent"

const RadiusSelector: React.FC<RadiusSelectorProps> = ({
  fromInventory,
  onViewAllDisclosures,
}): JSX.Element => {
  const [{ radius }, dispatch] = useContext(LocationContext)
  const [filtersState, dispatchFilters] = useContext(FiltersContext)
  const { tealPageData, swapInventoryFilterInQueue } = useTealiumContext()
  const { trackTealEvent } = useTealiumEvent()
  const steps: number[] = [25, 50, 100, 250, 500, 600]
  const position = steps.indexOf(radius) + 1

  const [pendingRadius, setPendingRadius] = useState(radius)

  // Find the closest step to the given value
  const findClosestStep = (value: number): number => {
    return steps.reduce((prev, curr) =>
      Math.abs(curr - value) < Math.abs(prev - value) ? curr : prev,
    )
  }

  // Snap to closest step while dragging
  const handleSliderChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const rawValue = Number(e.target.value)
    const snappedValue = findClosestStep(rawValue)
    setPendingRadius(snappedValue)
  }

  // Snap to closest step on commit
  const handleSliderCommit = () => {
    const distance = findClosestStep(pendingRadius)
    dispatch(setRadius(distance))

    if (fromInventory) {
      dispatchFilters(setSelectedFilterOption("radius", distance))
      swapInventoryFilterInQueue(
        {
          refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Radius|${distance}`,
        },
        `${tealPageData?.page_type}|${tealPageData?.page_name}|Radius|`,
      )
    } else {
      dispatchFilters(setSelectedFilterOption("radius", distance))
      trackTealEvent({
        tealium_event: "refinement",
        refinement_value: `${tealPageData?.page_type}|${tealPageData?.page_name}|Miles|${distance}`,
      })
    }
  }

  return (
    <div css={[tw`flex items-center text-center`]}>
      <DistanceSlider
        min={25}
        minValue={25}
        max={600}
        radius={pendingRadius}
        inputs
        maxValue={600}
        tabIndex={0}
        onChange={handleSliderChange}
        onMouseUp={handleSliderCommit}
        onTouchEnd={handleSliderCommit}
        fromInventory={fromInventory}
        onViewAllDisclosures={onViewAllDisclosures}
        position={position}
      />
    </div>
  )
}

export default RadiusSelector
